<template>
  <v-layout column id="review-and-deliver" class="full-height-container">
    <SavingDialog :opened.sync="savingDialogOpened" />

    <IdentityVerificationSettingsDialog
      :title="identityVerificationDialogTitle"
      :opened.sync="identityVerificationSettingsDialogOpened"
      :taxReturnId="taxReturn.id"
      :companyId="taxReturn.company_id"
    />

    <v-layout column class="text-xs-left content">
      <v-layout column>
        <v-layout class="mb-2 alert-box" align-start>
          <v-icon left class="primary--text material-icons-outlined"
            >info</v-icon
          >

          <div>
            <p>
              <sc-small>
                For this messaging, you can use customized words (variables).
                These variables will then be replaced with the actual values
                when the message is sent. For example, "{entity_name}" will be
                replaced with "ABC Corp" if the entity's name is ABC Corp.
              </sc-small>
            </p>

            <p class="mb-0">
              <sc-small>
                All of the available customized words (variables) can be found
                <a
                  href="https://helpcenter.soraban.com/customized-words-variables-new"
                  style="cursor: pointer"
                  target="_blank"
                >
                  here (opens in a new tab).
                </a>
              </sc-small>
            </p>
          </div>
        </v-layout>
      </v-layout>

      <v-layout column class="mt-4 form-control">
        <h2>Message template</h2>
        <v-autocomplete
          solo
          hide-details
          class="mt-1"
          :items="deliveryMessageTemplates"
          :item-text="item => item.name"
          item-value="id"
          :value="currentDeliveryMessageTemplate"
          @change="onTemplateChange"
          :disabled="!editable"
          label="Select template"
        />
      </v-layout>

      <v-tabs class="tabs" v-model="activeMessageTab" slider-color="primary">
        <v-tab key="initial-email">
          <span>Initial Email</span>
        </v-tab>
        <v-tab key="reminder-email">
          <span>Reminder Email</span>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeMessageTab" class="tabs-content-container">
        <v-tab-item key="initial-email">
          <v-layout column class="mt-4 form-control">
            <h2>Subject</h2>
            <v-text-field
              class="mt-1"
              v-model="subject"
              solo
              hide-details
              :disabled="!editable"
            />
          </v-layout>

          <v-layout column class="mt-4 form-control">
            <h2>Message</h2>

            <quill-editor
              v-model="message"
              :options="editorOption"
              class="delivery-message-input mt-2"
              :disabled="!editable"
            />
          </v-layout>
        </v-tab-item>

        <v-tab-item key="reminder-email">
          <v-layout column class="mt-4 form-control">
            <h2>Subject</h2>
            <v-text-field
              class="mt-1"
              v-model="reminderSubject"
              solo
              hide-details
              :disabled="!editable"
            />
          </v-layout>

          <v-layout column class="mt-4 form-control">
            <h2>Message</h2>

            <quill-editor
              v-model="reminderMessage"
              :options="editorOption"
              class="delivery-message-input mt-2"
              :disabled="!editable"
            />
          </v-layout>
        </v-tab-item>
      </v-tabs-items>

      <v-layout
        v-if="
          currentUser.firm_access == 'partner' && currentDeliveryMessageTemplate
        "
        class="mt-2"
        justify-end
      >
        <v-btn
          color="primary"
          @click="updateTemplate"
          :disabled="!editable"
          class="text-none"
        >
          Update Template
        </v-btn>
      </v-layout>

      <v-layout column class="mt-4">
        <v-layout align-center justify-space-between>
          <h2>
            Recipients
          </h2>

          <v-menu>
            <sc-button
              slot="activator"
              size="small"
              type="primary"
              class="text-none"
              :loading="updatingRecipient"
              :disabled="!editable"
            >
              <v-icon small class="white--text">add</v-icon>
              <span v-if="allRecipientNames.length === 0">
                Select Recipients
              </span>
              <span v-else>
                Select Additional Recipients
              </span>
            </sc-button>

            <v-list two-line subheader>
              <v-list-tile
                v-for="owner in currentCompany.owners"
                :key="owner.id"
                avatar
                @click="addRemoveRecipient(owner)"
                :disabled="
                  recipientNamesFromESignForms.some(
                    name => name === owner.full_name,
                  )
                "
              >
                <v-list-tile-action>
                  <v-checkbox
                    :input-value="
                      allRecipientNames.some(name => name === owner.full_name)
                    "
                    :disabled="
                      recipientNamesFromESignForms.some(
                        name => name === owner.full_name,
                      )
                    "
                  />
                </v-list-tile-action>

                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ owner.full_name }}
                    <span v-if="owner.id === currentCompany.user.id">
                      (Primary contact)
                    </span>
                  </v-list-tile-title>
                  <v-list-tile-sub-title>{{
                    getEmailByName(owner.full_name)[0] || owner.email
                  }}</v-list-tile-sub-title>
                </v-list-tile-content>

                <v-list-tile-action
                  v-if="
                    recipientNamesFromESignForms.some(
                      name => name === owner.full_name,
                    )
                  "
                >
                  <v-tooltip>
                    <v-icon slot="activator" color="grey lighten-1"
                      >info</v-icon
                    >

                    <span>Required for e-sign forms.</span>
                  </v-tooltip>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-layout>

        <div v-if="allRecipientNames.length === 0">
          <span class="error--text">
            * No recipients have been added to this delivery yet. Please add a
            recipient to deliver the tax return.
          </span>
        </div>

        <div
          v-else
          v-for="(recipientName, index) in allRecipientNames"
          class="recipient-container"
        >
          <v-layout row wrap>
            <v-flex xs6 class="px-3 py-2">
              <div class="font-weight-bold">{{ recipientName }}</div>

              <div
                class="pt-1 email"
                v-for="email in getEmailByName(recipientName)"
              >
                <sc-small>{{ email }}</sc-small>
              </div>
            </v-flex>

            <v-flex
              xs6
              class="px-3 py-2"
              v-if="getESignFormsByName(recipientName).length > 0"
            >
              <span class="font-weight-bold">Pages to sign:</span>
              <ul class="pt-1">
                <li v-for="form in getESignFormsByName(recipientName)">
                  <sc-small>
                    <span
                      v-if="
                        form.form_id === 'other' &&
                          !isEmpty(form.custom_form_name)
                      "
                      >{{ form.custom_form_name }}</span
                    >
                    <span v-else-if="getFormDefinitionById(form.form_id)">
                      {{ getFormDefinitionById(form.form_id).name }} ({{
                        form.form_id
                      }})
                    </span>
                    <span v-else>Unknown</span>
                  </sc-small>
                </li>
              </ul>
            </v-flex>
          </v-layout>
        </div>
      </v-layout>

      <v-layout column class="mt-4" v-if="taxReturn.k1_packages.length > 0">
        <v-layout align-center justify-space-between>
          <h2>
            K-1 Packages
          </h2>
        </v-layout>

        <v-data-table
          :headers="k1TableHeaders"
          :items="taxReturn.k1_packages"
          class="partners-table auto-shrink"
          :pagination.sync="k1TablePagination"
          hide-actions
        >
          <template v-slot:items="props">
            <td>
              {{ props.item.partner_name }}
            </td>

            <td class="text-xs-left">
              {{ props.item.partner_email }}
            </td>

            <td class="text-xs-left">
              {{ props.item.custom_package_name }}
            </td>
          </template>
        </v-data-table>
      </v-layout>

      <v-layout column class="mt-4">
        <h2>Options</h2>

        <div class="mb-1 mt-3">
          <v-checkbox
            label="Require identity verification via Stripe Identity Verification"
            v-model="stripeIdvCheckboxValue"
            :disabled="!editable"
            hide-details
            class="mt-0 pt-0 align-center"
            data-testid="stripe-identity-verification-checkbox"
          >
          </v-checkbox>
        </div>

        <!-- @Lisia TODO: Remove this when confirmed that we don't need it, depercaring SMS verification starting 2026-->
        <!-- <div class="mb-3">
          <v-checkbox
            v-if="
              taxReturnIdentityVerificationEnabled &&
                identityVerifications.length > 0
            "
            label="Enable identity verification via SMS"
            v-model="taxReturn.identity_verification_enabled"
            :disabled="!editable"
            hide-details
            class="mt-0 pt-0 align-center"
          >
            <div slot="append">
              <v-btn
                outline
                color="primary"
                size="small"
                class="text-none"
                :disabled="
                  !taxReturn.identity_verification_enabled || !editable
                "
                @click="openIdentityVerificationSettingsDialog"
              >
                <v-icon small class="mr-1 primary--text material-icons-outlined"
                  >settings</v-icon
                >
                Configure Identity Verification
              </v-btn>
            </div>
          </v-checkbox>
        </div> -->

        <v-layout
          class="mb-3"
          v-if="customPaymentLinkEnabled"
          align-start
          column
        >
          <div class="mb-2 font-weight-bold">Select Products:</div>
          <v-autocomplete
            v-model="selectedProducts"
            :items="products"
            item-text="name"
            return-object
            label="Select Products"
            multiple
            class="default-input mb-4"
            hide-details
            solo
          />
          <v-layout
            column
            class="mt-2"
            v-if="selectedProducts.length > 0"
            full-width
          >
            <v-layout align-center class="mb-2">
              <v-flex xs6 class="font-weight-bold">Product Name</v-flex>
              <v-flex xs6 class="font-weight-bold">Quantity</v-flex>
            </v-layout>
            <v-layout
              v-for="product in selectedProducts"
              :key="product.id"
              align-center
              class="mb-2"
            >
              <v-flex xs6>{{ product.name }}</v-flex>
              <v-flex xs6>
                <v-text-field
                  v-model="product.quantity"
                  type="number"
                  min="0"
                  class="default-input"
                  :class="{
                    'error--text': !product.quantity,
                  }"
                  hide-details
                  solo
                />
              </v-flex>
              <v-btn
                icon
                @click="removeProduct(product)"
                class="ml-2 error--text"
              >
                <v-icon>delete</v-icon>
              </v-btn>
            </v-layout>
          </v-layout>
        </v-layout>

        <v-layout class="mb-3" align-center v-else>
          <v-checkbox
            v-if="taxReturnPaymentEnabled && billingEnabled && stripeConnected"
            label="Enable Stripe payment"
            :input-value="Boolean(taxReturn.payment)"
            @change="togglePayment"
            :disabled="!editable || !hasMarkPaidPermission"
            hide-details
            class="mt-0 pt-0 payment-checkbox"
            data-testid="enable-stripe-payment-checkbox"
          />

          <v-layout
            v-if="taxReturn.payment"
            align-center
            class="payment-amount-container"
          >
            <v-divider vertical class="mx-3 my-2" />
            <v-layout
              v-if="changeable && editingPaymentAmount && hasMarkPaidPermission"
              align-center
            >
              <span class="mr-2">Payment amount:</span>
              <CurrencyInput
                :value="newPaymentAmount"
                @input="$e => (newPaymentAmount = $e)"
                :options="{ currency: 'USD' }"
                class="default-input"
                :class="{
                  error: !newPaymentAmount,
                }"
              />
              <v-btn
                class="primary--text"
                icon
                @click="saveEditPaymentAmount"
                :disabled="
                  newPaymentAmount === 0 ||
                    newPaymentAmount === taxReturn.payment.amount / 100
                "
              >
                <v-icon>check_circle</v-icon>
              </v-btn>
              <v-btn class="error--text" icon @click="cancelEditPaymentAmount">
                <v-icon>cancel</v-icon>
              </v-btn>
            </v-layout>
            <v-layout
              v-else-if="changeable && !editingPaymentAmount"
              align-center
            >
              <span class="mr-2">Payment amount:</span>
              <span class="mr-2">{{
                formatCurrency(taxReturn.payment.amount / 100)
              }}</span>
              <v-tooltip v-if="hasMarkPaidPermission" bottom>
                <v-btn
                  slot="activator"
                  class="primary--text"
                  icon
                  @click="toggleEditPaymentAmount"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
                <span>Edit</span>
              </v-tooltip>
            </v-layout>
            <v-layout v-else align-center>
              <span class="mr-2">Enter payment amount:</span>

              <CurrencyInput
                :value="taxReturn.payment.amount / 100"
                @input="$e => parseInt((taxReturn.payment.amount = $e * 100))"
                :options="{ currency: 'USD' }"
                :disabled="!editable || !hasMarkPaidPermission"
                class="default-input"
                :class="{
                  error: !taxReturn.payment.amount,
                }"
              />
              <v-spacer />
            </v-layout>
          </v-layout>
        </v-layout>
      </v-layout>

      <v-layout v-if="taxReturn.status === 'sent'" class="mt-3">
        <v-alert
          color="success"
          icon="check_circle"
          :value="true"
          outline
          style="flex: auto"
        >
          <b
            >This tax return has been delivered.
            {{
              taxReturn.payment
                ? `The invoice is ${taxReturn.payment.status}.`
                : ''
            }}</b
          >
        </v-alert>
      </v-layout>

      <v-btn
        class="ma-0 mt-3 mb-3 deliver-btn text-none"
        size="large"
        color="primary"
        :disabled="!isDeliverable"
        @click="deliver"
        data-testid="deliver-to-recipients-button"
      >
        <sc-h4>Deliver to Recipients</sc-h4>
        <v-icon class="white--text ml-2 send-icon material-icons-outlined"
          >send</v-icon
        >
      </v-btn>
    </v-layout>
  </v-layout>
</template>

<script>
import uniq from 'lodash/uniq';
import API from '@/api';
import axios from 'axios';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import { mapGetters } from 'vuex';
import IdentityVerificationSettingsDialog from '@/components/returns/IdentityVerificationSettingsDialog.vue';
import TaxReturnIdentityVerificationMixin from '@/mixins/TaxReturnIdentityVerificationMixin';
import TaxDetailMixin from '@/mixins/TaxDetailMixin';
import CurrencyInput from '@/components/returns/CurrencyInput';
import SavingDialog from '@/components/returns/SavingDialog';
import isEmpty from 'lodash/isEmpty';
// import posthog from 'posthog-js';

export default {
  mixins: [
    FirmPreferenceMixins,
    TaxReturnIdentityVerificationMixin,
    TaxDetailMixin,
  ],

  components: {
    IdentityVerificationSettingsDialog,
    CurrencyInput,
    SavingDialog,
  },

  props: {
    currentCompany: {
      type: Object,
      required: true,
    },

    taxReturn: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      formDefinitions: [],
      delivering: false,
      delivered: false,
      selectedProducts: [],

      deliveryMessageTemplates: [],
      currentDeliveryMessageTemplate: null,

      editorOption: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ indent: '-1' }, { indent: '+1' }],
            ['link'],
            [{ list: 'ordered' }, { list: 'bullet' }],
          ],
        },
        placeholder: 'Message',
        bounds: '.delivery-message-input',
      },

      activeMessageTab: 0,
      subject: '',
      message: '',
      reminderSubject: '',
      reminderMessage: '',

      identityVerificationSettingsDialogOpened: false,
      identityVerificationDialogTitle: undefined,
      identityVerifications: [],

      updatingRecipient: false,
      additionalRecipients: [],

      stripeConnected: false,

      // k1 packages
      k1TableHeaders: [
        {
          text: 'Partner name',
          align: 'left',
          value: 'partner_name',
          sortable: false,
        },
        {
          text: 'Partner email',
          value: 'partner_email',
          align: 'left',
          sortable: false,
        },
        {
          text: 'K-1 package',
          value: 'custom_package_name',
          align: 'left',
          sortable: false,
        },
      ],

      k1TablePagination: {
        rowsPerPage: -1,
      },
      editingPaymentAmount: false,
      newPaymentAmount: 0,

      savingDialogOpened: false,

      stripeIdvUserHasOverridden: false,
    };
  },

  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
      currentUser: 'user',
    }),

    customPaymentLinkEnabled() {
      return this.currentFirm.preferences.custom_payment_link_enabled;
    },

    products() {
      return this.currentFirm.preferences.custom_payment_link_products;
    },

    recipientNamesFromESignForms() {
      return uniq(
        this.taxReturn.e_sign_forms.flatMap(form => {
          return form.signatures
            .filter(signature => signature.id !== this.currentUser.id)
            .map(signature => signature.name);
        }),
      );
    },

    allRecipientNames() {
      return uniq(
        this.recipientNamesFromESignForms
          .concat(
            this.additionalRecipients.map(
              recipient => recipient.user.full_name,
            ),
          )
          .filter(user => user.id !== this.currentUser.id),
      );
    },

    hasMarkPaidPermission() {
      if (
        !this.currentFirm ||
        !this.currentFirm.preferences ||
        !this.currentFirm.preferences.tax_return_delivery_permissions_enabled
      ) {
        return true;
      }
      return (
        this.currentUser &&
        this.currentUser.permissions &&
        // 604 = tax_return_delivery.mark_paid permission
        this.currentUser.permissions.some(p => p.id === 604)
      );
    },

    editable() {
      return this.taxReturn.status === 'action_required';
    },

    changeable() {
      return (
        this.taxReturn.status !== 'action_required' &&
        this.taxReturn.payment &&
        this.taxReturn.payment.status === 'unpaid'
      );
    },

    hasRecipients() {
      return this.allRecipientNames.length > 0;
    },

    hasOptions() {
      return (
        this.taxReturnIdentityVerificationEnabled ||
        this.taxReturnStripeIDVEnabled
      );
    },

    isPaymentValid() {
      return (
        !this.taxReturn.payment ||
        (this.taxReturn.payment && this.taxReturn.payment.amount)
      );
    },

    isDeliverable() {
      return (
        this.editable &&
        !this.delivering &&
        this.hasRecipients &&
        this.isPaymentValid
      );
    },

    stripeIdvCheckboxValue: {
      get() {
        if (this.taxReturn.status === 'action_required') {
          return this.stripeIdvUserHasOverridden
            ? this.taxReturn.stripe_idv_enabled
            : this.taxReturnStripeIDVDefaultValueEnabled;
        }
        return this.taxReturn.stripe_idv_enabled;
      },
      set(value) {
        this.stripeIdvUserHasOverridden = true;
        this.$set(this.taxReturn, 'stripe_idv_enabled', value);
      },
    },
  },

  methods: {
    isEmpty,

    setMessageFields(template) {
      this.subject = template.subject;
      this.message = template.body;
      this.reminderSubject = template.reminder_subject;
      this.reminderMessage = template.reminder_message;
    },

    toggleEditPaymentAmount() {
      this.editingPaymentAmount = !this.editingPaymentAmount;
      this.newPaymentAmount = this.taxReturn.payment.amount / 100;
    },

    cancelEditPaymentAmount() {
      this.editingPaymentAmount = false;
      this.newPaymentAmount = this.taxReturn.payment.amount / 100;
    },

    async saveEditPaymentAmount() {
      try {
        const response = await API.taxReturn.updateTaxReturn({
          id: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
          payment_amount: this.newPaymentAmount * 100,
        });
        this.editingPaymentAmount = false;
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Payment amount updated.',
          });
          this.taxReturn.payment.amount = this.newPaymentAmount * 100;
        }
      } catch (e) {
        console.error(e);
      }
    },

    isRequiredToSignForms(recipientName) {
      return this.recipientNamesFromESignForms.some(
        name => name === recipientName,
      );
    },

    removeProduct(product) {
      this.selectedProducts = this.selectedProducts.filter(
        p => p.id !== product.id,
      );
    },

    getEmailByName(name) {
      return uniq(
        this.taxReturn.e_sign_forms
          .flatMap(form => {
            return form.signatures
              .filter(
                signature =>
                  signature.name === name &&
                  signature.userId !== this.currentUser.id,
              )
              .map(signature => {
                const user = this.currentCompany.owners.find(
                  user => user.id === signature.userId,
                );

                // Check if selectedSigner exists and has alternative signer settings
                if (
                  signature.selectedSigner &&
                  signature.selectedSigner.alternative_signer_id
                ) {
                  // Find the user with the alternative_signer_id
                  const alternativeUser = this.currentCompany.owners.find(
                    owner =>
                      owner.id ===
                      signature.selectedSigner.alternative_signer_id,
                  );
                  return alternativeUser ? alternativeUser.email : null;
                }

                return user && user.email;
              })
              .filter(email => email); // Remove undefined values
          })
          .concat(
            this.additionalRecipients
              .filter(recipient => recipient.user.full_name === name)
              .map(recipient => recipient.user.email),
          ),
      );
    },

    getESignFormsByName(name) {
      return this.taxReturn.e_sign_forms.filter(form => {
        return form.signatures.some(signature => signature.name === name);
      });
    },

    getFormDefinitionById(id) {
      return this.formDefinitions.find(form => form.id === id);
    },

    async getFormDefinitions() {
      const response = await API.signatureRequest.getFormDefinitions();

      this.formDefinitions = response.data.concat({
        id: 'other',
        name: 'Other',
        taxpayer_signature: null,
        spouse_signature: null,
      });
    },

    async openIdentityVerificationSettingsDialog($e, title) {
      this.identityVerificationSettingsDialogOpened = true;
      this.identityVerificationDialogTitle = !isEmpty(title)
        ? title
        : undefined;
    },

    async saveIdentityVerificationEnabled() {
      await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.taxReturn.company_id,
        identity_verification_enabled: this.taxReturn
          .identity_verification_enabled,
      });
    },
    async saveStripeIDVEnabled() {
      await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.taxReturn.company_id,
        stripe_idv_enabled: this.taxReturn.stripe_idv_enabled,
      });
    },

    async addRemoveRecipient(owner) {
      try {
        this.updatingRecipient = true;

        const existingRecipient = this.additionalRecipients.find(
          recipient => recipient.user.id === owner.id,
        );

        if (existingRecipient) {
          await API.taxReturnAdditionalRecipient.removeAdditionalRecipient({
            id: existingRecipient.id,
            taxReturnId: this.taxReturn.id,
            companyId: this.taxReturn.company_id,
          });
        } else {
          const response = await API.taxReturnAdditionalRecipient.addAdditionalRecipient(
            {
              taxReturnId: this.taxReturn.id,
              companyId: this.taxReturn.company_id,
              userId: owner.id,
            },
          );
        }
      } finally {
        this.updatingRecipient = false;

        await this.getAdditionalRecipients();
      }
    },

    async getAdditionalRecipients() {
      const response = await API.taxReturnAdditionalRecipient.listAdditionalRecipients(
        {
          taxReturnId: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
        },
      );

      this.additionalRecipients = response.data;
    },

    async deliver() {
      try {
        this.delivering = true;

        await this.checkIdentityVerificationsForUpdate(
          this.taxReturn.id,
          this.taxReturn.company_id,
        );

        if (
          this.taxReturn.identity_verification_enabled &&
          this.hasUnverifiedIdentityVerifications
        ) {
          this.openIdentityVerificationSettingsDialog(
            null,
            'Please confirm the information below and try delivery again.',
          );
        } else {
          await axios.post(`/tax_returns/${this.taxReturn.id}/deliver`, {
            company_id: this.taxReturn.company_id,
            subject: this.subject,
            message: this.message,
            reminder_subject: this.reminderSubject,
            reminder_message: this.reminderMessage,
            payment: this.taxReturn.payment,
            products: this.selectedProducts,
          });

          // posthog.capture('delivered tax return', {
          //   tax_return_id: this.taxReturn.id,
          //   company_id: this.currentCompany.id,
          //   identity_verification_enabled: this.taxReturn
          //     .identity_verification_enabled,
          //   payment_enabled: this.taxReturn.payment ? true : false,
          // });

          this.delivered = true;
          this.$emit('return-delivered');
        }
      } finally {
        this.delivering = false;
      }
    },

    togglePayment(enabled) {
      if (enabled) {
        this.taxReturn.payment = {
          amount: 0,
          currency: 'usd',
        };
      } else {
        this.taxReturn.payment = null;
      }
    },

    reset() {
      this.subject =
        this.taxReturn.subject ||
        'Your {tax_year} tax return is ready for review';
      this.message =
        this.taxReturn.message ||
        `<p>Hi {client_preferred_name},</p>
<p>We are pleased to inform you that your {tax_year} Tax Return is now available for your review. Please take a moment to review and sign the return by clicking on the link below. The link will also display any outstanding balance owed to the tax authority.</p>
<p>Once we receive the e-file authorization form (and payment), we will proceed with e-filing the tax return.</p>
<p>Thank you,</p>
<p>${this.currentFirm.name}</p>`;

      this.reminderSubject =
        this.taxReturn.customization.reminder_email_subject ||
        '[Reminder] Actions required for completing your {tax_year} tax filing.';
      this.reminderMessage =
        this.taxReturn.customization.reminder_email_message ||
        `<p>Hi {client_preferred_name},</p>
<p>This is a reminder to complete your {tax_year} tax return ({tax_return_name}). Please click the button below to open your tax return.</p>
<p>Thank you,</p>`;

      // Workaround fix for SOR-10941
      // Force re-render this vue component
      const element = document.getElementById('review-and-deliver');
      if (element) {
        element.style.display = 'none';

        requestAnimationFrame(() => {
          element.style.display = 'flex';
        });
      }
    },

    async checkStripeIntegration() {
      try {
        const response = await API.integration.checkStripeIntegration();

        this.stripeConnected = response.data.connected;
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
      }
    },

    async showSavingDialog() {
      if (this.savingDialogOpened) {
        return;
      }

      try {
        this.savingDialogOpened = true;
        await this.save();
      } finally {
        this.savingDialogOpened = false;
      }
    },

    async beforeClose() {
      await this.showSavingDialog();
      return true;
    },

    async save() {
      if (!this.editable || this.delivered) {
        return;
      }

      const response = await API.taxReturn.updateTaxReturn({
        id: this.taxReturn.id,
        companyId: this.taxReturn.company_id,
        subject: this.subject,
        message: this.message,
        reminder_subject: this.reminderSubject,
        reminder_message: this.reminderMessage,
        has_payment: this.taxReturn.payment ? true : false,
        payment_amount: this.taxReturn.payment
          ? this.taxReturn.payment.amount
          : null,
      });

      this.taxReturn.subject = response.data.subject;
      this.taxReturn.message = response.data.message;

      if (
        typeof response.data.customization.reminder_email_subject !==
        'undefined'
      ) {
        this.taxReturn.customization.reminder_email_subject =
          response.data.customization.reminder_email_subject;
      }

      if (
        typeof response.data.customization.reminder_email_message !==
        'undefined'
      ) {
        this.taxReturn.customization.reminder_email_message =
          response.data.customization.reminder_email_message;
      }

      this.$emit('return-updated', response.data);
    },

    onTemplateChange(templateId) {
      this.currentDeliveryMessageTemplate = templateId;
      const template = this.deliveryMessageTemplates.find(
        t => t.id === templateId,
      );
      if (template) {
        this.setMessageFields(template);
      }
    },

    async fetchDeliveryMessageTemplates() {
      try {
        const response = await axios.get(
          '/tax_return_message_templates/get_delivery_message_templates',
        );
        this.deliveryMessageTemplates = response.data || [];
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    },

    async updateTemplate() {
      if (!this.currentDeliveryMessageTemplate) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'No template selected.',
        });
        return;
      }

      const param = {
        subject: this.subject,
        body: this.message,
        reminder_subject: this.reminderSubject,
        reminder_message: this.reminderMessage,
      };

      try {
        const response = await axios.patch(
          `/tax_return_message_templates/${this.currentDeliveryMessageTemplate}`,
          param,
        );

        if (response.status === 200) {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Template updated successfully.',
            timeout: 3000,
          });

          await this.fetchDeliveryMessageTemplates();
        } else {
          throw new Error(`Unexpected status code: ${response.status}`);
        }
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Error updating template.',
        });
      }
    },
  },

  watch: {
    'taxReturn.identity_verification_enabled'(value) {
      this.saveIdentityVerificationEnabled();

      if (value) {
        this.openIdentityVerificationSettingsDialog();
      }
    },
    'taxReturn.stripe_idv_enabled'(value) {
      this.saveStripeIDVEnabled();
    },
  },

  async created() {
    if (this.taxReturn.status === 'action_required') {
      if (this.taxReturn.stripe_idv_enabled === true) {
        this.stripeIdvUserHasOverridden = true;
      } else if (
        this.taxReturnStripeIDVEnabled &&
        (this.taxReturn.stripe_idv_enabled === undefined ||
          this.taxReturn.stripe_idv_enabled === null ||
          this.taxReturn.stripe_idv_enabled === false)
      ) {
        this.$set(
          this.taxReturn,
          'stripe_idv_enabled',
          this.taxReturnStripeIDVDefaultValueEnabled,
        );
      }
    }

    await this.getFormDefinitions();

    await this.getAdditionalRecipients();

    await this.checkIdentityVerificationsForUpdate(
      this.taxReturn.id,
      this.taxReturn.company_id,
    );

    await this.checkStripeIntegration();

    await this.fetchDeliveryMessageTemplates();

    // If already have custom messages, use it
    // Otherwise, apply default template if set in firm preferences
    if (
      this.taxReturn.subject ||
      this.taxReturn.message ||
      this.taxReturn.customization.reminder_email_subject ||
      this.taxReturn.customization.reminder_email_message
    ) {
      this.subject = this.taxReturn.subject || '';
      this.message = this.taxReturn.message || '';
      this.reminderSubject =
        this.taxReturn.customization.reminder_email_subject || '';
      this.reminderMessage =
        this.taxReturn.customization.reminder_email_message || '';
    } else {
      const defaultTemplateId = this.taxReturnMessageDefaultTemplateId;

      // Apply default template if set in firm preferences
      if (defaultTemplateId) {
        const defaultTemplate = this.deliveryMessageTemplates.find(
          t => t.id === defaultTemplateId,
        );

        if (defaultTemplate) {
          this.currentDeliveryMessageTemplate = defaultTemplateId;

          this.setMessageFields(defaultTemplate);
        } else {
          this.reset();
        }
      } else {
        this.reset();
      }
    }
  },

  async beforeDestroy() {
    await this.beforeClose();
  },
};
</script>

<style lang="postcss" scoped>
.full-height-container {
  box-sizing: border-box;
  overflow: auto;
  min-height: calc(100vh - 60px);
  max-height: calc(100vh - 60px);
  background-color: var(--gray-100);
  border-top: 1px solid var(--gray-200);
}

.content {
  margin: 0 auto;
  max-width: 800px;
}

.delivery-message-input {
  overflow: visible;

  & >>> .ql-toolbar.ql-snow {
    height: 50px;
    display: flex;
    align-items: center;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border: 1px solid #ddd !important;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1) !important;
  }

  & >>> .ql-container.ql-snow {
    height: max-content;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1) !important;
    border: 1px solid #ddd !important;
    border-top: none !important;
  }

  & >>> .ql-container .ql-editor {
    min-height: 200px;
    max-height: 200px;
  }

  & >>> .ql-container .ql-editor p,
  & >>> .ql-container .ql-editor ul {
    padding-bottom: 8px;
  }
}

.recipient-container {
  margin-top: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #fff;
  font-size: 16px;
  padding: 16px 0px;

  & >>> h3 {
    font-size: 20px;
  }

  & >>> .flex:first-child {
    border-right: 1px solid #ddd;
  }
}

.email {
  word-break: break-all; /* Breaks long words or emails to the next line */
  white-space: normal; /* Ensure text wraps if needed */
}

.deliver-btn {
  font-weight: bold;
  font-size: 19px;
  height: 48px;
  box-shadow: none !important;
}

.send-icon {
}

.payment-checkbox {
  flex-grow: 0;
}

.payment-amount-container {
  & >>> .v-input {
    border: 1px solid #ccc;
  }

  & >>> .v-input.error {
    border: 1px solid red;
  }
}

.partners-table {
  border: 1px solid #ddd;
  border-radius: 8px;
  margin: 8px 0px;
  overflow: hidden;

  & >>> .v-table thead tr {
    height: 48px;
    background-color: #eee;
  }

  & >>> .v-table thead th {
    font-weight: bold;
  }

  & >>> .v-table tbody td,
  & >>> .v-table thead th {
    padding: 0px 10px;
  }
}

.form-control {
  margin-top: 16px;

  & >>> .v-text-field {
  }

  & >>> .v-text-field .v-input__slot {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1) !important;
    border: 1px solid #ddd;
    border-radius: 8px;
  }
}

.form-control .error--text {
  & >>> .v-input__slot {
    border: 1px solid var(--red-500) !important;
  }
}

.content {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 24px;
  margin: 32px auto;
  box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}

.alert-box {
  background-color: #136df412;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #136df412;
}
</style>
